import { ReactNode, useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { BigNumber } from 'bignumber.js';
import useDrfqBbo from '@paradigm/features/src/unified-rfqs/contexts/use-drfq-bbo';

import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { getUserTicker } from '@paradigm/store/auth';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';

import reducer, {
  changeAccount,
  changeQuantity,
  changeQuoteTimer,
  changePrice,
  INITIAL_STATE,
  TwoWayValue,
  changeLegPrice,
  NullableTwoWay,
  resetLegPrices,
  clearPrice,
} from '#/unified-rfqs/ui/drfq/quote-builder/form-reducer';
import {
  useQuotePrefill,
  useQuotePrefillActions,
} from '#/unified-rfqs/ui/drfq/quote-builder/prefill-context';

import { getOppositeSide, Side } from '#/unified-rfqs/entities/shared';
import { findBaseProductCode } from '#/products/domain';
import { useDefaultAccountsActions } from '#/preferences/default-accounts/context';
import { useDrfqOrderbook } from '#/unified-rfqs/contexts/drfqs';
import { useDrfqMakerOrders } from '../drfq-maker-orderbook-context';
import { useIsMultiLegStrategy } from '../../hooks/shared';
import { createView, QuoteBuilderViewProvider } from './view';
import { QuoteBuilderSubmit } from './submit-context';
import { useQuoteBuilderModalState } from './modal-context';
import { ChangeLegPriceArgs } from './form-reducer';
import { useQuoteBuilderPrefill } from './hooks';
import { getValidValueOrNull, isValueEmpty } from './utils';
import EstimatedPriceModalProvider from './estimated-price-modal-context';

export interface QuoteBuilderActions {
  changeAccount: (account: string) => void;
  changeQuoteTimer: (quoteTimer: string) => void;
  changeQuantity: (quantity: string) => void;
  changePrice: (price: NullableTwoWay) => void;
  resetLegPrices: () => void;
  clearTwoWayPrice: () => void;
  changeLegPrice: (legIndex: ChangeLegPriceArgs) => void;
  fillPriceFromBbo: () => void;
  fillPriceFromExistingHiddenLimitOrders: () => boolean;
  fillPriceFromPreviousInput: () => boolean;
  changeLegPricesFromBbo: (
    side: Side,
    isEmptyValueAllowed?: boolean,
    fillFromMarkPrice?: boolean,
  ) => void;
  fillLegPricesWithMarkPrice: (sides?: Side[]) => void;
}

export const [useQuoteBuilderActions, ActionsCtxProvider] =
  createSafeContext<QuoteBuilderActions>('QuoteBuilderActions');

interface Props {
  children: ReactNode;
  drfq: Drfq;
}

export function QuoteBuilderProvider({ children, drfq }: Props) {
  const bbo = useDrfqBbo(drfq.id);
  const quotePrefill = useQuotePrefill(drfq.id);
  const quotePrefillActions = useQuotePrefillActions();
  const { didSubmit } = useSubmitted();
  const myDesk = useSelector(getUserTicker)!;
  const makerOrders = useDrfqMakerOrders();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { legs, venue } = drfq;
  const isMultiLegStrategy = useIsMultiLegStrategy(drfq);
  const { getDefaultAccount } = useDefaultAccountsActions();
  const makerLegPrices = useMemo(
    () => getOrdersLegPrices(makerOrders, drfq),
    [makerOrders, drfq],
  );
  const [drfqBids, drfqAsks] = useDrfqOrderbook(drfq.id);

  const [state, dispatch] = useReducer(
    reducer,
    INITIAL_STATE,
    (initialState) => ({
      ...initialState,
      price: [
        makerOrders.bid?.price ?? '',
        makerOrders.ask?.price ?? '',
      ] as TwoWayValue,
      account:
        getDefaultAccount(venue, findBaseProductCode(drfq.product_codes)) ??
        undefined,
      quantity: drfq.quantity,
      legsPrice: makerLegPrices,
    }),
  );

  const actions: QuoteBuilderActions = useMemo(
    () => ({
      changeAccount: (account) => dispatch(changeAccount(account)),
      changeQuoteTimer: (quoteTimer) => dispatch(changeQuoteTimer(quoteTimer)),
      changeQuantity: (quantity) => dispatch(changeQuantity(quantity)),
      clearTwoWayPrice: () => {
        dispatch(clearPrice());
      },
      changePrice: (price) => {
        dispatch(changePrice(price));
        quotePrefillActions.changePrice(price, drfq.id);
      },
      resetLegPrices: () => {
        dispatch(resetLegPrices());
      },
      changeLegPrice: (indexLeg) => {
        dispatch(changeLegPrice(indexLeg));
        quotePrefillActions.changeLegPrice(indexLeg, drfq.id);
      },
      fillPriceFromBbo: () => {
        if (bbo == null) return;
        actions.changePrice([
          getValidValueOrNull(bbo.best_bid?.price),
          getValidValueOrNull(bbo.best_ask?.price),
        ]);

        if (isMultiLegStrategy) {
          actions.changeLegPricesFromBbo('BUY');
          actions.changeLegPricesFromBbo('SELL');
        }
      },
      fillLegPricesWithMarkPrice: (sides: Side[] = ['BUY', 'SELL']) => {
        if (bbo == null) return;
        sides.forEach((side) =>
          actions.changeLegPricesFromBbo(side, false, true),
        );
      },
      fillPriceFromPreviousInput: () => {
        let hasFilled = false;
        if (quotePrefill?.price != null) {
          actions.changePrice(quotePrefill.price);
          hasFilled = true;
        }
        if (isMultiLegStrategy && quotePrefill?.legs != null) {
          Object.values(quotePrefill.legs).forEach(actions.changeLegPrice);
          hasFilled = true;
        }
        return hasFilled;
      },
      fillPriceFromExistingHiddenLimitOrders: () => {
        const myBid_ = drfqBids.find(
          (bid) => bid.desk === myDesk || bid.type === 'HIDDEN',
        );
        const myAsk_ = drfqAsks.find(
          (ask) => ask.desk === myDesk || ask.type === 'HIDDEN',
        );

        actions.changePrice([
          getValidValueOrNull(myBid_?.price),
          getValidValueOrNull(myAsk_?.price),
        ]);

        return (
          getValidValueOrNull(myBid_?.price) != null ||
          getValidValueOrNull(myAsk_?.price) != null
        );
      },
      changeLegPricesFromBbo: (
        bboSide,
        isEmptyValueAllowed = true,
        fillFromMarkPrice = false,
      ) => {
        if (bbo == null) return;
        const { best_ask, best_bid, legs: bbo_legs } = bbo;

        const bboBest = bboSide === 'BUY' ? best_bid : best_ask;
        const quantity = new BigNumber(bboBest?.quantity ?? 0);

        // Applicable only when filling from best bid/ask
        if (quantity.isZero() && !fillFromMarkPrice) return;
        if (bbo_legs.length !== drfq.legs.filter((leg) => !leg.isHedge).length)
          return;

        const indexLegs = bbo_legs.map((bboLeg, index) => {
          const rfqLeg = drfq.legs[index]!;
          const { best_bid_price, best_ask_price, mark_price } = bboLeg;
          const isSellLegSide = rfqLeg.side === 'SELL';

          let value = bboSide === 'BUY' ? best_bid_price : best_ask_price;

          let side = bboSide;
          if (isSellLegSide) {
            value = bboSide === 'BUY' ? best_ask_price : best_bid_price;
            side = getOppositeSide(bboSide);
          }

          if (fillFromMarkPrice) {
            value = mark_price;
          }
          return { index, side, value };
        });

        const hasEmptyValue = (leg: ChangeLegPriceArgs) =>
          isValueEmpty(leg.value);

        if (indexLegs.some(hasEmptyValue) && !isEmptyValueAllowed) return;

        indexLegs.forEach(actions.changeLegPrice);
      },
    }),
    [
      quotePrefillActions,
      drfq.id,
      drfq.legs,
      bbo,
      isMultiLegStrategy,
      quotePrefill?.price,
      quotePrefill?.legs,
      drfqBids,
      drfqAsks,
      myDesk,
    ],
  );

  const isPrefillingQuotes = useQuoteBuilderPrefill({
    bbo,
    actions,
    didSubmit,
  });
  const view = useMemo(
    () =>
      createView({
        state,
        bbo,
        legPricingEnabled,
        myDesk,
        isPrefillingQuotes,
        productCodes: [...drfq.product_codes],
        legs,
        isLegPricesLoading: false,
        isAnonymousRfq: drfq.is_taker_anonymous,
      }),
    [
      state,
      bbo,
      legPricingEnabled,
      myDesk,
      isPrefillingQuotes,
      drfq.product_codes,
      drfq.is_taker_anonymous,
      legs,
    ],
  );

  return (
    <QuoteBuilderViewProvider value={view}>
      <ActionsCtxProvider value={actions}>
        <QuoteBuilderSubmit>
          <EstimatedPriceModalProvider>{children}</EstimatedPriceModalProvider>
        </QuoteBuilderSubmit>
      </ActionsCtxProvider>
    </QuoteBuilderViewProvider>
  );
}

interface MakerOrders {
  readonly bid: Order | null;
  readonly ask: Order | null;
}

export function getOrdersLegPrices(
  { bid, ask }: MakerOrders,
  drfq: Drfq,
): NullableTwoWay[] {
  return drfq.legs
    .filter((leg) => !leg.isHedge)
    .map((leg, index) => {
      const askLegPrice = ask?.legs[index]?.price ?? null;
      const bidLegPrice = bid?.legs[index]?.price ?? null;
      const isSell = leg.side === 'SELL';

      return [
        isSell ? askLegPrice : bidLegPrice,
        isSell ? bidLegPrice : askLegPrice,
      ];
    });
}
